import React, { useEffect, useMemo, useState } from 'react';
import { useTitle } from '@arema/components/Hooks';
import { addCommas, bindClick, fromUnix, fromUnixLocal } from '@arema/components/Util';
import { DateTime } from 'luxon';
import { Button, Groupper, Header, Table, Icon } from 'react-frontier2';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { PapeletaPdvData } from '../../AdminClasses';
import { NotFound } from '../../components';
import CorteDetailModal from '../pdv_concilia/CorteDetailModal';
import CreateDepositModal from '../pdv_concilia/CreateDepositModal';
import API from '../../API';
import { Money } from '@arema/components';

interface PapeletaPdvProps{
	
}

var PapeletaPdv = (props: PapeletaPdvProps)=>{
	var { t } = useTranslation();
	var { setTitle } = useTitle();
	var params = useParams<{ day: string }>();
	var [data, setData] = useState<PapeletaPdvData[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [viewingCorte, setViewingCorte] = useState<number>(null);
	var [viewingDeposit, setViewingDeposit] = useState<number>(null);
	
	var params_day = useMemo(()=>{
		if(!params.day) return null;
		var d = DateTime.fromFormat(params.day, 'yyyy-MM-dd');
		if(!d.isValid) return null;
		return d.set({
			hour: 12,
			minute: 0,
			second: 0,
		}).toSeconds();
	}, [params]);

	useEffect(()=>{
		setTitle(`Papeleta PDV ${fromUnix(params_day).toFormat('dd/MM/yy')}`)
		API.getPapeletaPdv(params_day).then(res=>{
			if(res.error) return setLoadError(res.message);
			setData(res.data);
		}).catch(err=>{
			return setLoadError(t('common.unexpected_code', { code: 'LCL-PPLPV-1' }));
		})
	}, [params_day]);

	if(!params_day) return <NotFound />
	if(!data || loadError){
		return <Header loading={!data && !loadError} text={t(`common.${loadError ? 'error' : 'loading'}`)} subtext={loadError} />
	}
	
	return <div className='ar concilia-papeleta-pdv' style={{ width: 700, margin: 'auto' }}>
		<Header text='Papeleta Taquillas' subtext={fromUnix(params_day).toFormat('dd/MM/yyyy')} />
		<div style={{ textAlign: 'center', color: 'gray', marginBottom: 10 }}>
			Haz click en un corte o deposito para ver mas información
		</div>
		{data.map(p=>{
			var total_cortes = {
				amount_sales: 0,
				amount_deposit: 0,
			}
			for(var i of p.cortes){
				total_cortes.amount_deposit += i.amount_deposit;
				total_cortes.amount_sales += i.amount_sales;
			}

			return <React.Fragment key={`PV-${p.pdv_id}`}>
				<Groupper fitted title={`[${p.pdv_id}] ${p.pdv_name}`} style={{ marginBottom: 15 }} titleRight={(
					<Button text='Ver' as={Link} to={`/pdv/${p.pdv_id}/conciliacion`} target={'_blank'} />
				)}>
					<Table>
						<Table.Head title='Eventos' titleSize='small' data={[['ID', 'Evento', 'Corte', 'Cantidad']]} />
						<Table.Body>
							{p.events.map((e, i, arr)=>{
								var rowspan = e.cortes.length + (e.cortes.length>1 ? 1 : 0) + 1;
								return <>
									<Table.Row selectable>
										<Table.Cell collapsing compact rowSpan={rowspan} as={Link} to={`/events/${e.event_id}/balance`} target='_blank' value={e.event_id} />
										<Table.Cell compact rowSpan={rowspan} as={Link} to={`/events/${e.event_id}/balance`} target='_blank' value={e.event_name} />
									</Table.Row>
									{e.cortes.map(c=>(
										<Table.Row selectable onClick={bindClick(setViewingCorte, c.corte_id)} key={`CRTR-${e.event_id}-${c.corte_id}`}>
											<Table.Cell collapsing value={c.corte_id} />
											<Table.Cell collapsing value={<Money value={c.amount} />} />
										</Table.Row>
									))}
									{e.cortes.length>1 && (
										<Table.Row>
											<Table.Cell style={{ fontWeight: 'bold' }} value={t('common.total')} />
											<Table.Cell value={<Money value={e.cortes.reduce((a,b)=>a+b.amount, 0)} />} />
										</Table.Row>
									)}
									{i<(arr.length-1) && (
										<Table.Divider />
									)}
								</>
							})}
						</Table.Body>
						<Table.Footer>
							<Table.Row>
								<Table.Cell colSpan={3} style={{ textAlign: 'right', fontWeight: 'bold' }} value={t('common.total')} />
								<Table.Cell value={<Money value={p.events.reduce((a,b)=>a+b.cortes.reduce((c,d)=>c+d.amount, 0), 0)} />} />
							</Table.Row>
						</Table.Footer>
					</Table>
					<Table>
						<Table.Head title='Depositos' titleSize='small' data={[['ID', 'Fecha', 'Tipo', 'Cuenta', 'Corte', 'Cantidad']]} />
						<Table.Body>
							{p.deposits.map((d, i, arr)=>{
								var rowspan = d.cortes.length + (d.cortes.length>1 ? 1 : 0) + 1;
								return <>
									<Table.Row>
										<Table.Cell collapsing rowSpan={rowspan} value={d.deposit_id} />
										<Table.Cell rowSpan={rowspan} value={fromUnixLocal(d.date_deposit).toFormat('dd/MM/yy')} />
										<Table.Cell collapsing rowSpan={rowspan} value={t(`pdv_deposit_type.type_${d.deposit_type}`)} />
										<Table.Cell collapsing rowSpan={rowspan} value={d.source_id ? d.source_name : (
											<Icon name='minus' color='gray' />
										)} />
									</Table.Row>
									{d.cortes.map(c=>(
										<Table.Row selectable onClick={bindClick(setViewingCorte, c.corte_id)} key={`CRDP-${d.deposit_id}-${c.corte_id}`}>
											<Table.Cell collapsing value={c.corte_id} />
											<Table.Cell collapsing value={<Money value={c.amount} />} />
										</Table.Row>
									))}
									{d.cortes.length>1 && (
										<Table.Row>
											<Table.Cell style={{ fontWeight: 'bold' }} value={t('common.total')} />
											<Table.Cell value={<Money value={d.cortes.reduce((a,b)=>a+b.amount, 0)} />} />
										</Table.Row>
									)}
									{i<(arr.length-1) && (
										<Table.Divider />
									)}
								</>
							})}
						</Table.Body>
						<Table.Footer>
							<Table.Row>
								<Table.Cell colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }} value={t('common.total')} />
								<Table.Cell value={<Money value={p.deposits.reduce((a,b)=>a+b.cortes.reduce((c,d)=>c+d.amount, 0), 0)} />} />
							</Table.Row>
						</Table.Footer>
					</Table>
					<Table>
						<Table.Head title='Cortes' titleSize='small' data={[['ID', 'Vendido', 'Depositado', 'Restante']]} />
						<Table.Body>
							{p.cortes.map(c=>(
								<Table.Row>
									<Table.Cell value={c.corte_id} />
									<Table.Cell collapsing value={<Money value={c.amount_sales} />} />
									<Table.Cell collapsing value={<Money value={c.amount_deposit} />} />
									<Table.Cell collapsing value={<Money value={c.amount_sales-c.amount_deposit} />} />
								</Table.Row>
							))}
						</Table.Body>
						<Table.Footer>
							<Table.Row>
								<Table.Cell style={{ textAlign: 'right', fontWeight: 'bold' }} value={t('common.total')} />
								<Table.Cell value={<Money value={total_cortes.amount_sales} />} />
								<Table.Cell value={<Money value={total_cortes.amount_deposit} />} />
								<Table.Cell value={<Money value={total_cortes.amount_sales-total_cortes.amount_deposit} />} />
							</Table.Row>
						</Table.Footer>
					</Table>
				</Groupper>
			</React.Fragment>
		})}
		<CorteDetailModal
			open={!!viewingCorte}
			corte_id={viewingCorte}
			onClose={bindClick(setViewingCorte)}
		/>
		<CreateDepositModal
			open={!!viewingDeposit}
			depositId={viewingDeposit}
			pdvId={null}
			onSave={null}
			onClose={bindClick(setViewingDeposit)}
		/>
	</div>
}

export default PapeletaPdv;